import { defineStore } from 'pinia';
import { ref, computed, watch, onScopeDispose } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useLoggerStore } from '@/stores/LoggerStore';
import HunterStatsService, { computeBaseStats } from '@/services/HunterStatsService';
import { statsFromQsos, mergeStats } from '@/lib/hunterStatsAggregate';
import { normalizeReference } from '@/utils/potaReference';
import { potaParksLogger } from '@/utils/loggers';
import { QSO_ID_PREFIX } from '@/constants';
import type { LocalParkStat, HunterParkCsvRow, HunterParkStat } from '@/types';

// Single reactive source of POTA hunter stats for the spot views.
//
// `count` is HAMRS-only (QSOs across all logbooks). `everHunted` additionally honors
// parks present in an imported pota.app CSV. Local counts are split into:
//   - baseStats: all logbooks EXCEPT the open one (recomputed on logbook switch)
//   - openStats: the open logbook, re-derived live from loggerStore.qsoList
// merged on read, so logging/deleting a QSO updates counts immediately without a full
// DB rescan and without double-counting the open logbook.
export const useHunterStatsStore = defineStore('hunterStats', () => {
  // baseStats holds per-park counts for every logbook EXCEPT `baseExcludesId` (the open
  // logbook, whose QSOs are overlaid live via openStats). Tracking which logbook base
  // currently excludes lets the overlay stay consistent: during a logbook switch the async
  // base rescan and the open logbook momentarily disagree, and overlaying then would
  // double-count, so we fall back to base alone until they line up again.
  const baseStats = ref<Map<string, LocalParkStat>>(new Map());
  const baseExcludesId = ref<string | null | undefined>(undefined);
  const csvParks = ref<Map<string, HunterParkCsvRow>>(new Map());
  const csvLoaded = ref(false);
  const localLoaded = ref(false);
  const lastImportedAt = ref(0);
  // Cheap monotonic counter bumped whenever reconciled data changes, so a non-reactive
  // consumer (the Leaflet overlay) can watch a single primitive instead of deep maps.
  const version = ref(0);

  const loggerStore = useLoggerStore();
  const openLogbookId = computed(() => loggerStore.currentLogbook?._id ?? null);
  // Live per-park stats for the open logbook, derived straight from qsoList. No debounce:
  // qsoList only changes on save/delete (not keystrokes), and reducing one logbook is cheap,
  // so deriving it keeps openStats exactly in step with qsoList (no stale-overlay window).
  const openStats = computed(() => statsFromQsos(loggerStore.qsoList));

  const localStats = computed(() => {
    // Overlay the live open-logbook stats only when base was computed to EXCLUDE exactly the
    // open logbook (steady state), or before the first scan when base is still empty.
    // Mid-switch the two disagree and base still contains the open logbook's QSOs, so use
    // base alone to avoid a transient double-count.
    const overlay =
      baseExcludesId.value === undefined || baseExcludesId.value === openLogbookId.value
        ? openStats.value
        : new Map<string, LocalParkStat>();
    return mergeStats(baseStats.value, overlay);
  });
  const csvCount = computed(() => csvParks.value.size);

  watch([localStats, csvParks], () => {
    version.value++;
  });

  // Recompute baseStats (all logbooks except the open one). Gated by a request id so a
  // slow earlier scan (e.g. the bootstrap scan for the initial logbook) can't overwrite a
  // newer one - a stale completion would otherwise re-include the open logbook and
  // double-count it. baseExcludesId is advanced together with baseStats so localStats only
  // overlays once the two are consistent.
  let baseStatsRequest = 0;
  async function refreshBaseStats(logbookId?: string | null): Promise<void> {
    const requestId = ++baseStatsRequest;
    try {
      const next = await computeBaseStats(logbookId ?? null);
      if (requestId === baseStatsRequest) {
        baseStats.value = next;
        baseExcludesId.value = logbookId ?? null;
      }
    } catch (error) {
      if (requestId === baseStatsRequest) {
        potaParksLogger('Hunter base-stats recompute failed', error);
      }
    }
  }

  // The open logbook's QSOs move from base into the live openStats overlay, so rescan base
  // (excluding it) whenever the open logbook changes.
  watch(openLogbookId, (id) => {
    void refreshBaseStats(id);
  });

  // Keep base fresh when QSOs change in ANY OTHER logbook - live HAMRS sync, bulk
  // move/edit, or an ADIF import into a non-open logbook. The open logbook is already
  // covered live by openStats, so its own changes are skipped. Debounced so a bulk write
  // (e.g. a large import or a sync burst) triggers a single rescan, not one per doc.
  const refreshBaseDebounced = useDebounceFn(() => {
    void refreshBaseStats(openLogbookId.value);
  }, 800);
  const qsoChangesSub = HunterStatsService.subscribeToQsoChanges((changedId) => {
    const openId = openLogbookId.value;
    if (openId && changedId.startsWith(`${QSO_ID_PREFIX}:${openId}:`)) return;
    refreshBaseDebounced();
  });
  // Cancel the live feed if this store's scope is disposed (HMR / test isolation) so a
  // stale instance can't leave a listener running.
  onScopeDispose(() => {
    qsoChangesSub.cancel();
  });

  function countFor(reference: string): number {
    return localStats.value.get(normalizeReference(reference))?.count ?? 0;
  }

  function everHunted(reference: string): boolean {
    const key = normalizeReference(reference);
    return (localStats.value.get(key)?.count ?? 0) > 0 || csvParks.value.has(key);
  }

  function statsFor(reference: string): HunterParkStat {
    const key = normalizeReference(reference);
    const local = localStats.value.get(key);
    const csv = csvParks.value.get(key);
    const count = local?.count ?? 0;
    return {
      reference: key,
      count,
      firstDate: local?.firstDate,
      lastDate: local?.lastDate,
      inCsv: !!csv,
      csvQsos: csv?.qsos,
      everHunted: count > 0 || !!csv,
    };
  }

  function setCsvRows(rows: HunterParkCsvRow[]): void {
    const map = new Map<string, HunterParkCsvRow>();
    for (const row of rows) map.set(normalizeReference(row.reference), row);
    csvParks.value = map;
    csvLoaded.value = true;
  }

  // Best-effort startup load: never blocks bootstrap. Tracks the in-flight promise so
  // concurrent callers (bootstrap + settings-page mount) share one run, and resets it on
  // failure so a transient error doesn't permanently disable a later retry.
  let initPromise: Promise<void> | null = null;
  async function runInit(): Promise<void> {
    const meta = await HunterStatsService.getCsvMetadata();
    lastImportedAt.value = meta.lastImportedAt;
    setCsvRows(await HunterStatsService.loadCsvRows());
    await refreshBaseStats(loggerStore.currentLogbook?._id ?? null);
    localLoaded.value = true;
  }
  function initializeInBackground(): Promise<void> {
    if (!initPromise) {
      initPromise = runInit().catch((error) => {
        potaParksLogger('Hunter stats init failed', error);
        initPromise = null;
      });
    }
    return initPromise;
  }

  // Persist rows already parsed by the import form (so the CSV is parsed only once).
  async function importParsedRows(rows: HunterParkCsvRow[]): Promise<number> {
    await HunterStatsService.persistCsvRows(rows);
    setCsvRows(rows);
    lastImportedAt.value = Date.now();
    return rows.length;
  }

  async function clearCsv(): Promise<void> {
    await HunterStatsService.clearCsv();
    setCsvRows([]);
    lastImportedAt.value = 0;
  }

  return {
    csvParks,
    csvLoaded,
    localLoaded,
    lastImportedAt,
    version,
    localStats,
    csvCount,
    countFor,
    everHunted,
    statsFor,
    initializeInBackground,
    importParsedRows,
    clearCsv,
  };
});
